import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Award,
  Users,
  TrendingUp,
  CheckCircle,
  Star,
  Crown,
  Medal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const TierCard = ({ tier, index, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className={`relative rounded-sm overflow-hidden ${
      tier.name === 'Gold' ? 'ring-2 ring-bronze' : ''
    }`}
    data-testid={`tier-${tier.name.toLowerCase()}`}
  >
    {tier.name === 'Gold' && (
      <div className="absolute top-0 right-0 bg-bronze text-white text-xs font-medium px-3 py-1">
        {t('partners.mostPopular')}
      </div>
    )}
    <div className={`${tier.color} p-6 text-white text-center`}>
      <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
        {tier.icon}
      </div>
      <h3 className="text-2xl font-serif font-bold">{tier.name}</h3>
      <p className="text-sm mt-2 opacity-80">{t('partners.adhesion')}: {tier.adhesion}</p>
    </div>
    <div className="bg-white p-6 border border-t-0 border-stone-200">
      <ul className="space-y-3">
        <li className="flex items-center gap-3 text-stone-700 text-sm">
          <CheckCircle className="h-5 w-5 text-emerald flex-shrink-0" />
          {t(tier.b1Key)}
        </li>
        <li className="flex items-center gap-3 text-stone-700 text-sm">
          <CheckCircle className="h-5 w-5 text-emerald flex-shrink-0" />
          {t(tier.b2Key)}
        </li>
        <li className="flex items-center gap-3 text-stone-700 text-sm">
          <CheckCircle className="h-5 w-5 text-emerald flex-shrink-0" />
          {t(tier.b3Key)}
        </li>
        <li className="flex items-center gap-3 text-stone-700 text-sm">
          <CheckCircle className="h-5 w-5 text-emerald flex-shrink-0" />
          {t(tier.b4Key)}
        </li>
      </ul>
      <Button 
        asChild
        variant={tier.name === 'Gold' ? 'default' : 'outline'}
        className={`w-full mt-6 ${
          tier.name === 'Gold' 
            ? 'bg-emerald hover:bg-emerald-light text-white' 
            : 'border-emerald text-emerald hover:bg-emerald hover:text-white'
        }`}
        data-testid={`tier-btn-${tier.name.toLowerCase()}`}
      >
        <Link to="/contato">{t('partners.wantToBe')} {tier.name}</Link>
      </Button>
    </div>
  </motion.div>
);

const RuleCard = ({ rule, index, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="bg-white/10 backdrop-blur-sm p-8 rounded-sm"
    data-testid={`ascension-rule-${index}`}
  >
    <div className="w-14 h-14 rounded-sm bg-bronze/20 flex items-center justify-center text-bronze mb-6">
      {rule.icon}
    </div>
    <h3 className="text-xl font-serif font-semibold mb-4">
      {t(rule.titleKey)}
    </h3>
    <p className="text-stone-300">
      {t(rule.descKey)}
    </p>
  </motion.div>
);

const Parceiros = () => {
  const { t } = useLanguage();

  const tierBronze = { 
    name: 'Bronze', 
    icon: <Medal className="h-8 w-8" />,
    adhesion: t('partners.free'),
    color: 'bg-amber-700',
    b1Key: 'partners.benefit1Bronze',
    b2Key: 'partners.benefit2Bronze',
    b3Key: 'partners.benefit3Bronze',
    b4Key: 'partners.benefit4Bronze'
  };

  const tierSilver = { 
    name: 'Silver', 
    icon: <Star className="h-8 w-8" />,
    adhesion: `R$ 50.000 ${t('partners.optional')}`,
    color: 'bg-stone-400',
    b1Key: 'partners.benefit1Silver',
    b2Key: 'partners.benefit2Silver',
    b3Key: 'partners.benefit3Silver',
    b4Key: 'partners.benefit4Silver'
  };

  const tierGold = { 
    name: 'Gold', 
    icon: <Crown className="h-8 w-8" />,
    adhesion: `R$ 100.000 ${t('partners.optional')}`,
    color: 'bg-amber-500',
    b1Key: 'partners.benefit1Gold',
    b2Key: 'partners.benefit2Gold',
    b3Key: 'partners.benefit3Gold',
    b4Key: 'partners.benefit4Gold'
  };

  const rules = [
    {
      titleKey: 'partners.rule1Title',
      descKey: 'partners.rule1Desc',
      icon: <Users className="h-6 w-6" />
    },
    {
      titleKey: 'partners.rule2Title',
      descKey: 'partners.rule2Desc',
      icon: <TrendingUp className="h-6 w-6" />
    },
    {
      titleKey: 'partners.rule3Title',
      descKey: 'partners.rule3Desc',
      icon: <Award className="h-6 w-6" />
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="parceiros-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block"
            >
              {t('partners.tag')}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6"
            >
              {t('partners.title')} <span className="gold-sheen">{t('partners.titleHighlight')}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-stone-600 mb-8"
            >
              {t('partners.desc')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button 
                asChild
                className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
                data-testid="parceiros-cta-primary"
              >
                <Link to="/contato" className="flex items-center gap-2">
                  {t('partners.cta')}
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-24 md:py-32 bg-white" data-testid="tiers-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('partners.tiersTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('partners.tiersTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <TierCard tier={tierBronze} index={0} t={t} />
            <TierCard tier={tierSilver} index={1} t={t} />
            <TierCard tier={tierGold} index={2} t={t} />
          </div>
        </div>
      </section>

      {/* Ascension Rules */}
      <section className="py-24 md:py-32 bg-emerald text-white" data-testid="ascension-rules">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('partners.ascensionTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">
              {t('partners.ascensionTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {rules.map((rule, index) => (
              <RuleCard key={rule.titleKey} rule={rule} index={index} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Award className="h-16 w-16 text-bronze mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('partners.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('partners.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="parceiros-final-cta"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('partners.cta')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Parceiros;
